import { BUSINESS_TYPE_LABELS } from '@crm/shared';
import { Button, cn, Input, Label, Spinner, Textarea } from '@crm/ui';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  Globe,
  LayoutDashboard,
  PartyPopper,
  RefreshCw,
  Share2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useCompany } from '@/modules/company';
import {
  LANDING_ROUTES,
  type LandingConfig,
  useLandingConfig,
  useSaveLandingConfig,
} from '@/modules/landing/api';
import { useBannerTemplates } from '@/modules/templates';
import { toErrorMessage } from '../../auth/utils';
import { useAdvanceWizard, useWizardState } from '../api';
import { StylePicker } from './style-picker';
import { WaitlistScreen } from './waitlist-screen';
import { WizardFooter } from './wizard-footer';

type Form = Omit<LandingConfig, 'publicUrl'>;
type Slide = Form['slides'][number];
type Fase = 'config' | 'banner' | 'concluido';

const ORDINAIS = ['primeiro', 'segundo', 'terceiro', 'quarto', 'quinto', 'sexto'];
const TITULO_MAX = 50;
const TEXTO_MAX = 100;

function ordinal(indice: number) {
  return ORDINAIS[indice] ?? `${indice + 1}º`;
}

/** Troca o placeholder pelo tipo do estabelecimento, ja em minusculo. */
function comTipo(titulo: string, tipo: string | undefined) {
  return titulo.replaceAll('{tipo de estabelecimento}', (tipo ?? 'estabelecimento').toLowerCase());
}

/**
 * Ultimo passo: a landing page do proprio salao. Primeiro o estilo e o
 * endereco; depois, banner por banner (um por vez, igual a foto de cada
 * categoria no passo de Servicos) - a quantidade e o texto padrao de cada um
 * vem dos banners prontos que o SaaS admin configura no Assistente de
 * Configuracoes. Ao terminar o ultimo, uma tela de fechamento antes de ir
 * para o painel.
 */
export function LandingStep({
  onDone,
  onFaseChange,
}: {
  onDone: () => void;
  /** A tela final e um fechamento, nao mais um passo - o cabecalho do passo
      (titulo + dica), que quem chama exibe por cima, some nela. */
  onFaseChange?: (concluido: boolean) => void;
}) {
  const company = useCompany();
  const config = useLandingConfig();
  const banners = useBannerTemplates();
  const save = useSaveLandingConfig();
  const advance = useAdvanceWizard();
  const wizardState = useWizardState();

  const [form, setForm] = useState<Form | null>(null);
  const [fase, setFase] = useState<Fase>('config');
  const [bannerIndex, setBannerIndex] = useState(0);
  const [fotoIndices, setFotoIndices] = useState<number[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [linkCopiado, setLinkCopiado] = useState(false);
  // Qual modelo (do Assistente de Configuracoes) esta emprestando titulo e
  // texto pra cada banner - comeca pareado (banner N com o modelo N), mas os
  // botoes Anterior/Proximo deixam trocar por outro modelo qualquer.
  const [modeloIndices, setModeloIndices] = useState<number[]>([]);

  useEffect(() => {
    onFaseChange?.(fase === 'concluido');
  }, [fase, onFaseChange]);

  const modelos = banners.data?.data ?? [];

  useEffect(() => {
    if (!config.data || !banners.data || !company.data || form) return;

    const modelosData = banners.data.data;
    const { publicUrl: _ignored, slides: slidesSalvos, ...rest } = config.data;
    const fotos = modelosData.map((modelo) => modelo.image).filter(Boolean);
    // A foto que vem pre-selecionada e sorteada entre todas as disponiveis -
    // senao, quem nao mexe em nada, a maioria, sairia sempre com a mesma
    // primeira foto do banner 1, a mesma segunda no banner 2...
    const indicesSorteados = modelosData.map(() =>
      fotos.length > 0 ? Math.floor(Math.random() * fotos.length) : 0,
    );

    const slides: Slide[] = modelosData.map((modelo, indice) => {
      const salvo = slidesSalvos[indice];
      if (salvo) return salvo;
      return {
        image: fotos[indicesSorteados[indice] as number] ?? modelo.image,
        // O texto padrao do template pode vir mais longo que o limite atual
        // do campo - trunca aqui, senao o contador nasce negativo (ex.:
        // 103/50) sem a pessoa ter digitado nada.
        title: comTipo(
          modelo.title,
          company.data?.businessType && BUSINESS_TYPE_LABELS[company.data.businessType],
        ).slice(0, TITULO_MAX),
        subtitle: modelo.subtitle.slice(0, TEXTO_MAX),
      };
    });

    setForm({ ...rest, isPublished: true, slides });
    setFotoIndices(indicesSorteados);
    setModeloIndices(modelosData.map((_, i) => i));
  }, [config.data, banners.data, company.data, form]);

  if (config.isLoading || banners.isLoading || company.isLoading || !form) {
    return (
      <div className="flex justify-center py-8">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  const set = <K extends keyof Form>(key: K, value: Form[K]) =>
    setForm((current) => (current ? { ...current, [key]: value } : current));

  function atualizarSlide(indice: number, campo: Partial<Slide>) {
    setForm((atual) =>
      atual
        ? { ...atual, slides: atual.slides.map((s, i) => (i === indice ? { ...s, ...campo } : s)) }
        : atual,
    );
  }

  const fotosDisponiveis = modelos.map((modelo) => modelo.image).filter(Boolean);

  /** Preenche imagem, titulo e texto do banner atual com um modelo do
      Assistente de Configuracoes - e assim que Anterior/Proximo funcionam. */
  function aplicarModelo(indice: number) {
    const modelo = modelos[indice];
    if (!modelo) return;
    setModeloIndices((atual) => {
      const copia = [...atual];
      copia[bannerIndex] = indice;
      return copia;
    });
    const indiceFoto = fotosDisponiveis.indexOf(modelo.image);
    if (indiceFoto >= 0) {
      setFotoIndices((atual) => {
        const copia = [...atual];
        copia[bannerIndex] = indiceFoto;
        return copia;
      });
    }
    atualizarSlide(bannerIndex, {
      image: modelo.image,
      title: comTipo(
        modelo.title,
        company.data?.businessType && BUSINESS_TYPE_LABELS[company.data.businessType],
      ).slice(0, TITULO_MAX),
      subtitle: modelo.subtitle.slice(0, TEXTO_MAX),
    });
  }

  function bannerAnterior() {
    if (modelos.length === 0) return;
    const atual = modeloIndices[bannerIndex] ?? 0;
    aplicarModelo((atual - 1 + modelos.length) % modelos.length);
  }

  function bannerProximo() {
    if (modelos.length === 0) return;
    const atual = modeloIndices[bannerIndex] ?? 0;
    aplicarModelo((atual + 1) % modelos.length);
  }

  /** So troca a foto - titulo e texto ficam como estao, diferente de
      Anterior/Proximo, que trocam o banner inteiro (foto + titulo + texto). */
  function trocarImagem() {
    if (fotosDisponiveis.length === 0) return;
    const atual = fotoIndices[bannerIndex] ?? 0;
    const proxima = (atual + 1) % fotosDisponiveis.length;
    setFotoIndices((indices) => {
      const copia = [...indices];
      copia[bannerIndex] = proxima;
      return copia;
    });
    atualizarSlide(bannerIndex, { image: fotosDisponiveis[proxima] ?? '' });
  }

  async function finalizar() {
    if (!form) return;
    setEnviando(true);
    setErro(null);
    try {
      await save.mutateAsync(form);
      setFase('concluido');
    } catch (error) {
      setErro(toErrorMessage(error));
    } finally {
      setEnviando(false);
    }
  }

  function avancarConfig() {
    if (!form) return;
    setErro(null);
    if (!form.slug.trim()) {
      setErro('Defina o endereco da pagina antes de continuar.');
      return;
    }
    if (modelos.length === 0) {
      finalizar();
      return;
    }
    setBannerIndex(0);
    setFase('banner');
  }

  function avancarBanner() {
    if (!form) return;
    setErro(null);
    if (!form.slides[bannerIndex]?.title?.trim()) {
      setErro('De um titulo pra esse banner antes de continuar.');
      return;
    }
    if (bannerIndex < modelos.length - 1) {
      setBannerIndex((indice) => indice + 1);
      return;
    }
    finalizar();
  }

  function voltarBanner() {
    setErro(null);
    if (bannerIndex > 0) {
      setBannerIndex((indice) => indice - 1);
      return;
    }
    setFase('config');
  }

  async function compartilhar() {
    if (!form) return;
    const url = `${window.location.origin}${LANDING_ROUTES.publicPage(form.slug)}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: form.headline || 'Agende seu horario', url });
      } catch {
        // Cancelou o compartilhamento - nada a fazer.
      }
      return;
    }
    await navigator.clipboard.writeText(url);
    setLinkCopiado(true);
    setTimeout(() => setLinkCopiado(false), 2000);
  }

  async function concluirWizard() {
    await advance.mutateAsync({ step: 'LANDING_PAGE' });
    onDone();
  }

  if (fase === 'concluido') {
    // A fila de espera troca o fechamento normal por essa tela - decidido
    // aqui, antes mesmo de fechar o ultimo passo (o advance so acontece no
    // "Continuar" logo abaixo, pra nao sumir com esta tela). Quem esta na
    // fila nunca ve o painel - so esta mesma tela, sempre que logar de novo.
    if (wizardState.data?.waitlistEnabled) {
      return (
        <div className="space-y-6">
          <WaitlistScreen />
          <div className="flex justify-center">
            <Button onClick={concluirWizard} disabled={advance.isPending}>
              {advance.isPending ? <Spinner className="h-4 w-4" /> : <Check className="h-4 w-4" />}
              Continuar
            </Button>
          </div>
        </div>
      );
    }

    const url = `${window.location.origin}${LANDING_ROUTES.publicPage(form.slug)}`;

    return (
      <div className="space-y-6 py-4 text-center">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-3xl bg-primary/10">
          <PartyPopper className="h-10 w-10 text-primary" />
        </div>
        <div className="space-y-2">
          <h2 className="text-lg font-semibold">Cadastro Realizado!</h2>
          <p className="text-sm text-muted-foreground">
            Parabéns, seus cadastros iniciais já foram realizados com sucesso. Agora você já pode
            compartilhar a página do seu estabelecimento, realizar agendamentos, consultar
            relatórios e muito mais...
          </p>
        </div>

        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">Esta é a página do seu estabelecimento</p>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 break-all font-medium text-primary hover:underline"
          >
            <Globe className="h-4 w-4 shrink-0" />
            {url.replace(/^https?:\/\//, '')}
          </a>
        </div>

        <div className="flex flex-col gap-2 pt-4 sm:flex-row">
          <Button className="w-full sm:flex-1" variant="outline" onClick={compartilhar}>
            <Share2 className="h-4 w-4" />
            {linkCopiado ? 'Link copiado!' : 'Compartilhar página'}
          </Button>
          <Button
            className="w-full sm:flex-1"
            onClick={concluirWizard}
            disabled={advance.isPending}
          >
            {advance.isPending ? (
              <Spinner className="h-4 w-4" />
            ) : (
              <LayoutDashboard className="h-4 w-4" />
            )}
            Ir para o painel
          </Button>
        </div>
      </div>
    );
  }

  if (fase === 'banner') {
    const slide = form.slides[bannerIndex];
    const ultimoBanner = bannerIndex >= modelos.length - 1;

    return (
      <div className="space-y-4 pb-20 sm:pb-0">
        {erro ? <p className="text-center text-sm text-destructive">{erro}</p> : null}

        <h3 className="text-center text-base font-medium">
          Agora escolha o {ordinal(bannerIndex)} banner
        </h3>

        {/* Preview igual ao hero da landing publica (mesmo gradiente, mesmo
            estilo de texto) - navegar aqui ja troca foto, titulo e texto
            juntos, puxando de um modelo real do Assistente de Configuracoes. */}
        <div className="relative aspect-[4/5] w-full overflow-hidden rounded-card bg-muted sm:aspect-video">
          {slide?.image ? (
            <img src={slide.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
          ) : null}
          <div
            className={cn(
              'pointer-events-none absolute inset-0 flex flex-col justify-end p-6',
              slide?.image && 'bg-gradient-to-t from-black/85 via-black/45 to-transparent',
            )}
          >
            {slide?.subtitle ? (
              <p
                className={cn(
                  'text-[11px] font-semibold uppercase tracking-[0.3em]',
                  slide?.image ? 'text-white/75' : 'text-muted-foreground',
                )}
              >
                {slide.subtitle}
              </p>
            ) : null}
            <p
              className={cn(
                'mt-3 text-3xl font-semibold leading-[1.15] tracking-tight',
                slide?.image && 'text-white',
              )}
            >
              {slide?.title}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Button type="button" variant="outline" onClick={bannerAnterior}>
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </Button>
          <Button type="button" variant="outline" onClick={trocarImagem}>
            <RefreshCw className="h-4 w-4" />
            Trocar imagem
          </Button>
          <Button type="button" variant="outline" onClick={bannerProximo}>
            Próximo
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label htmlFor="banner-titulo">Título do banner</Label>
            <span className="text-xs text-muted-foreground">
              {(slide?.title ?? '').length}/{TITULO_MAX}
            </span>
          </div>
          <Input
            id="banner-titulo"
            maxLength={TITULO_MAX}
            value={slide?.title ?? ''}
            onChange={(event) => atualizarSlide(bannerIndex, { title: event.target.value })}
          />
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label htmlFor="banner-texto">Texto do banner</Label>
            <span className="text-xs text-muted-foreground">
              {(slide?.subtitle ?? '').length}/{TEXTO_MAX}
            </span>
          </div>
          <Textarea
            id="banner-texto"
            rows={3}
            maxLength={TEXTO_MAX}
            value={slide?.subtitle ?? ''}
            onChange={(event) => atualizarSlide(bannerIndex, { subtitle: event.target.value })}
          />
        </div>

        <WizardFooter justify="between">
          <Button className="flex-1 sm:flex-none" variant="outline" onClick={voltarBanner}>
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
          <Button className="flex-1 sm:flex-none" onClick={avancarBanner} disabled={enviando}>
            {enviando ? <Spinner className="h-4 w-4" /> : null}
            {ultimoBanner ? 'Concluir' : 'Avançar'}
            {enviando ? null : <ArrowRight className="h-4 w-4" />}
          </Button>
        </WizardFooter>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-20 sm:pb-0">
      {erro ? <p className="text-center text-sm text-destructive">{erro}</p> : null}

      <div className="space-y-2">
        <Label>Escolha um estilo</Label>
        <StylePicker value={form.accent} onChange={(accent) => set('accent', accent)} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="slug">Escolha o endereço da sua página</Label>
        <div className="flex items-center gap-1.5 overflow-x-auto px-1 py-2 font-medium text-primary">
          <Globe className="h-4 w-4 shrink-0" />
          <span className="whitespace-nowrap text-sm">
            {window.location.origin}
            {LANDING_ROUTES.publicPage('').replace(/\/$/, '')}/
          </span>
          <Input
            id="slug"
            value={form.slug}
            onChange={(event) =>
              set('slug', event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))
            }
            className="h-auto min-w-24 flex-1 rounded-none border-0 border-input border-b bg-transparent px-1 py-0.5 text-sm font-medium text-primary hover:bg-transparent focus-visible:border-primary focus-visible:bg-transparent focus-visible:ring-0"
          />
        </div>
      </div>

      <WizardFooter justify="center">
        <Button className="w-full sm:w-1/2" onClick={avancarConfig} disabled={enviando}>
          {enviando ? <Spinner className="h-4 w-4" /> : null}
          Avançar
          {enviando ? null : <ArrowRight className="h-4 w-4" />}
        </Button>
      </WizardFooter>
    </div>
  );
}
